import {
  GridOptions,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { ColDef } from "ag-grid-community/dist/lib/entities/colDef";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useRef } from "react";

import { IgButton, IgTableWrapper } from "@intelligems/ig-theme";
import { MetricArrayType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { useGraphValueFormatter } from "../../../../api/formatters/dashboard";
import {
  BarMetricData,
  HistogramMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../../../types/analytics";
import { GraphConfig } from "../../Graphs/Utils/graphConfigs";

interface MetricDetailsTableProps {
  data?:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[]
    | StackedAreaMetricData[];
  config: GraphConfig;
  graphMetricTotals?: MetricArrayType;
  rawDatasetData: MetricArrayType;
}

export const MetricDetailsTable = ({
  data,
  config,
  graphMetricTotals,
  rawDatasetData,
}: MetricDetailsTableProps) => {
  const gridRef = useRef<AgGridReact>(null);
  const graphValueFormatter = useGraphValueFormatter();

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 100,
      resizable: false,
      cellStyle: { fontSize: "12px" },
      menuTabs: [],
      suppressMovable: true,
    };
  }, []);

  const pinnedTotalRows = useMemo(() => {
    const resultRow = { xAxisKey: "Total", metricLabels: config.metrics };

    config?.metricTableColumnKeys?.forEach((metric, index) => {
      const total = graphMetricTotals![0][metric];
      Object.assign(resultRow, {
        [config.metrics.length === 1
          ? "value"
          : config.metricTableColumnKeys && config.metricTableColumnKeys[index]
            ? config.metricTableColumnKeys[index]
            : config.metrics[index]]: total,
      });
    });
    return [resultRow];
  }, [config, graphMetricTotals]);

  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        headerName: "Date",
        headerClass: "header-custom",
        field: "xAxisKey",
        cellStyle: {
          justifyContent: "flex-start",
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value;
        },
      },
      ...config!.metricTableColumnKeys!.map((metric) => {
        return {
          headerName: config?.legendFormatFunction
            ? config.legendFormatFunction(metric)
            : metric,
          field: config!.metricTableColumnKeys!.length === 1 ? "value" : metric,
          valueFormatter: (params: ValueFormatterParams) => {
            return (
              graphValueFormatter(false, params.value, config) ?? params.value
            );
          },
        };
      }),
    ];
    const totalColumn = {
      headerName: "Total",
      valueGetter: (params: ValueGetterParams) => {
        let finalVal;
        if (config.metrics.length === 1) {
          finalVal = params.data.value;
        } else {
          finalVal = config.metrics.reduce(
            (acc, metric) => acc + params.data[metric],
            0
          );
        }
        return graphValueFormatter(false, finalVal, config);
      },
    };
    if (config.metrics.length > 1 && config.title[0] !== "AOV") {
      return [...cols, totalColumn];
    } else return cols;
  }, [data, config]);

  const gridOptions = useMemo<GridOptions>(() => {
    return {
      initialWidth: 200,
      rowData: data,
    };
  }, []);

  const onCopyClick = () => {
    gridRef.current?.api.selectAll();
    gridRef.current?.api.copyToClipboard();
  };

  useEffect(() => {
    gridRef.current?.api?.redrawRows();
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <Grid
      item
      container
      direction={"column"}
      rowSpacing={2}
      sx={{
        width: "100%",
        "& .ag-header-cell": {
          fontSize: "12px",
        },
        "& .ag-root-wrapper": {
          border: "1px solid rgba(0,0,0, .12) !important",
        },
      }}
    >
      <Grid item>
        <IgTableWrapper sx={{ height: "300px" }}>
          <AgGridReact
            ref={gridRef}
            // suppressContextMenu={true}
            suppressCsvExport={true}
            defaultColDef={defaultColDef}
            rowData={data}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            suppressDragLeaveHidesColumns={true}
            rowSelection={"multiple"}
            pinnedBottomRowData={pinnedTotalRows}
            // groupIncludeTotalFooter={true}
          />
        </IgTableWrapper>
      </Grid>
      <Grid container justifyContent={"end"} item mb={4}>
        <IgButton size={"small"} variant={"contained"} onClick={onCopyClick}>
          Copy Table Values
        </IgButton>
      </Grid>
    </Grid>
  );
};
